import type { CSSProperties, Key, ReactNode, RefObject } from "react";

import { useRef } from "react";

import clsx from "clsx";
import {
	Button,
	Group,
	Label,
	ListBox,
	ListBoxItem,
	Popover,
	Select,
	SelectValue,
	Tag,
	TagGroup,
	TagList,
} from "react-aria-components";
import { useController, useFormContext } from "react-hook-form";
import { useResizeObserver } from "usehooks-ts";

import { ArrowDownIcon } from "../../../icons/arrow-down";
import { CloseOutlineFilled } from "../../../icons/close-outline-filled";
import { SuccessIcon } from "../../../icons/success";

import styles from "./multi-select-input.module.css";

type MultiSelectOption = {
	id: string;
	label: string;
};

type MultiSelectInputProps = {
	className?: string;
	description?: string;
	isDisabled?: boolean;
	label: string;
	name: string;
	onChange?: (values: string[]) => void;
	options: readonly MultiSelectOption[];
	placeholder: string;
};

export function MultiSelectInput({
	className,
	description,
	isDisabled,
	label,
	name,
	onChange,
	options,
	placeholder,
}: MultiSelectInputProps): ReactNode {
	const { control } = useFormContext();
	const { field, fieldState } = useController({ control, name });
	const triggerRef = useRef<HTMLDivElement>(null);
	const { width: triggerWidth } = useResizeObserver({
		box: "border-box",
		ref: triggerRef as RefObject<HTMLDivElement>,
	});

	const value: string[] = Array.isArray(field.value) ? (field.value as string[]) : [];
	const isInvalid = Boolean(fieldState.error);

	return (
		<Select<MultiSelectOption, "multiple">
			className={clsx(styles.wrapper, className)}
			isDisabled={isDisabled}
			isInvalid={isInvalid}
			onChange={(next) => {
				field.onChange(next);
				onChange?.(next as string[]);
			}}
			placeholder={placeholder}
			selectionMode="multiple"
			value={value}
		>
			<Label className={clsx("caption", "bold", styles.label)}>{label}</Label>
			<Group
				className={clsx(styles.trigger, isInvalid && styles.invalidField)}
				ref={triggerRef}
			>
				<Button className={styles.triggerButton}>
					<span aria-hidden className={styles.arrow}>
						<ArrowDownIcon data-testid="arrow-down-icon" />
					</span>
				</Button>
				<SelectValue<MultiSelectOption> className={styles.value}>
					{({ isPlaceholder, selectedItems, state }) => {
						if (isPlaceholder) {
							return <span className={styles.placeholder}>{placeholder}</span>;
						}
						return (
							<TagGroup
								aria-label={label}
								onRemove={(keys: Set<Key>) => {
									if (Array.isArray(state.value)) {
										state.setValue(
											state.value.filter((k: string) => {
												return !keys.has(k);
											}),
										);
									}
								}}
							>
								<TagList
									className={styles.tagList}
									items={selectedItems.filter(
										(item): item is MultiSelectOption => {
											return item != null;
										},
									)}
								>
									{(item) => {
										return (
											<Tag
												className={styles.tag}
												id={item.id}
												textValue={item.label}
											>
												<span>{item.label}</span>
												<Button
													aria-label={`Remove ${item.label}`}
													className={styles.tagRemove}
													slot="remove"
												>
													<CloseOutlineFilled />
												</Button>
											</Tag>
										);
									}}
								</TagList>
							</TagGroup>
						);
					}}
				</SelectValue>
			</Group>
			{isInvalid ? (
				<span className={clsx("caption", styles.errorMessage)}>
					{fieldState.error?.message}
				</span>
			) : (
				<span className={clsx("caption", styles.description)}>{description}</span>
			)}
			<Popover
				className={styles.popover}
				placement="bottom"
				style={
					triggerWidth
						? ({ "--trigger-width": `${triggerWidth}px` } as CSSProperties)
						: undefined
				}
				triggerRef={triggerRef}
			>
				<ListBox<MultiSelectOption> className={styles.listbox} items={options}>
					{(item) => {
						return (
							<ListBoxItem
								className={styles.option}
								id={item.id}
								textValue={item.label}
							>
								<span aria-hidden className={styles.checkbox}>
									<SuccessIcon className={styles.tick} />
								</span>
								<span>{item.label}</span>
							</ListBoxItem>
						);
					}}
				</ListBox>
			</Popover>
		</Select>
	);
}
